import { useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, RotateCcw, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useCancellationRefundsQuery,
  useProcessCancellationRefundsMutation,
} from "@/features/events/api";
import { useMeQuery } from "@/features/organizers/api";
import { useT } from "@/lib/i18n";

const AUTO_CONTINUE_DELAY_MS = 10_000;

/**
 * İptal edilen etkinliğin otomatik iade durumu. Owner / Manager sayfayı açık
 * tuttukça bekleyen iadeler bu sayfadan da ilerletilir (sunucu trafik yokken uyur).
 */
export function CancellationRefundsCard({ slug }: { slug: string }) {
  const t = useT("eventDetail.refunds");
  const me = useMeQuery();
  const role = me.data?.role;
  const canRefund = role === "Owner" || role === "Manager";

  const query = useCancellationRefundsQuery(slug, {
    enabled: Boolean(role),
    poll: !canRefund,
  });
  const processMutation = useProcessCancellationRefundsMutation(slug);
  const summary = query.data;
  const pending = summary?.pending ?? 0;
  const { mutate, isPending } = processMutation;

  useEffect(() => {
    if (!canRefund || pending === 0 || isPending) return;
    const timer = window.setTimeout(() => mutate({ retryFailed: false }), AUTO_CONTINUE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [canRefund, pending, isPending, mutate]);

  if (!summary) return null;

  const done = pending === 0 && summary.failed === 0;
  const parts = [
    summary.succeeded > 0 && t("succeeded", { count: summary.succeeded }),
    summary.skipped > 0 && t("skipped", { count: summary.skipped }),
    pending > 0 && t("pending", { count: pending }),
    summary.failed > 0 && t("failed", { count: summary.failed }),
  ].filter(Boolean);

  const retry = () =>
    mutate(
      { retryFailed: true },
      {
        onSuccess: () => toast.success(t("retryStarted")),
        onError: (err) => toast.error(err.message),
      },
    );

  return (
    <Card className="mt-4">
      <CardHeader className="py-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          {done ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          ) : pending > 0 ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <TriangleAlert className="h-4 w-4 text-destructive" />
          )}
          {t("title")}
        </CardTitle>
        <CardDescription className="text-xs">
          {t("total", { count: summary.total })}
          {parts.length > 0 ? ` · ${parts.join(" · ")}` : null}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 pb-4 pt-0 text-xs">
        <p className="text-muted-foreground">
          {done ? t("allDone") : pending > 0 ? t(canRefund ? "inProgress" : "inProgressReadOnly") : t("needsAction")}
        </p>

        {summary.problems.length > 0 && (
          <ul className="divide-y rounded-md border">
            {summary.problems.map((p) => (
              <li key={p.orderNumber} className="space-y-0.5 px-3 py-2">
                <div className="flex flex-wrap items-center gap-x-2">
                  <Link to={`/orders/${p.orderNumber}`} className="font-mono font-medium hover:underline" dir="ltr">
                    {p.orderNumber}
                  </Link>
                  <span className="text-muted-foreground">{p.buyerName}</span>
                </div>
                {p.lastError && <p className="text-destructive">{p.lastError}</p>}
              </li>
            ))}
          </ul>
        )}

        {canRefund && summary.failed > 0 && (
          <div className="space-y-2">
            <p className="text-muted-foreground">{t("checkProvider")}</p>
            <Button size="sm" variant="outline" onClick={retry} disabled={isPending}>
              {isPending ? (
                <Loader2 className="me-1 h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="me-1 h-4 w-4" />
              )}
              {t("retry")}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
